import { useEffect, useState } from "react";
import { useCreate, useGetIdentity, useNotify, useUpdate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { Agency, AgencyBrandProfile } from "../types";
import {
  TONE_VOCABULARY,
  parseBrandProfile,
  safeParseBrandProfile,
  type BrandProfile,
} from "./brandProfile";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export type FormState = {
  display_name: string;
  tagline: string;
  primary: string;
  accent: string;
  agent_name: string;
  phone_display: string;
  email: string;
  website_display: string;
  adjectives: string[];
  notes: string;
};

export const EMPTY_FORM: FormState = {
  display_name: "",
  tagline: "",
  primary: "",
  accent: "",
  agent_name: "",
  phone_display: "",
  email: "",
  website_display: "",
  adjectives: [],
  notes: "",
};

const formFromBrand = (brand: BrandProfile | null): FormState => ({
  display_name: brand?.identity?.display_name ?? "",
  tagline: brand?.identity?.tagline ?? "",
  primary: brand?.palette?.primary ?? "",
  accent: brand?.palette?.accent ?? "",
  agent_name: brand?.contact?.agent_name ?? "",
  phone_display: brand?.contact?.phone_display ?? "",
  email: brand?.contact?.email ?? "",
  website_display: brand?.contact?.website_display ?? "",
  adjectives: [...(brand?.tone?.adjectives ?? [])],
  notes: brand?.tone?.notes ?? "",
});

/** Build the sparse BrandProfile document from the form (empty fields drop). */
export const brandFromForm = (
  form: FormState,
  previous: BrandProfile | null,
): Record<string, unknown> => {
  const brand: Record<string, unknown> = { schema_version: 1 };

  const identity: Record<string, unknown> = {};
  if (form.display_name.trim())
    identity.display_name = form.display_name.trim();
  if (form.tagline.trim()) identity.tagline = form.tagline.trim();
  if (Object.keys(identity).length) brand.identity = identity;

  const palette: Record<string, unknown> = {};
  if (form.primary.trim()) palette.primary = form.primary.trim();
  if (form.accent.trim()) palette.accent = form.accent.trim();
  if (Object.keys(palette).length) brand.palette = palette;

  const contact: Record<string, unknown> = {};
  if (form.agent_name.trim()) contact.agent_name = form.agent_name.trim();
  if (form.phone_display.trim())
    contact.phone_display = form.phone_display.trim();
  if (form.email.trim()) contact.email = form.email.trim();
  if (form.website_display.trim())
    contact.website_display = form.website_display.trim();
  if (Object.keys(contact).length) brand.contact = contact;

  const tone: Record<string, unknown> = {};
  if (form.adjectives.length) tone.adjectives = form.adjectives;
  if (form.notes.trim()) tone.notes = form.notes.trim();
  if (Object.keys(tone).length) brand.tone = tone;

  // Logo (and fonts) are managed from the agency portal, which owns asset
  // uploads — staff edits must not drop them. Saving REPLACES the whole
  // brand document, so every section this dialog does not render has to be
  // carried across explicitly; `brandProfile.test.ts` pins the list.
  if (previous?.logo) brand.logo = previous.logo;
  if (previous?.poster_logo) brand.poster_logo = previous.poster_logo;
  if (previous?.fonts) brand.fonts = previous.fonts;
  if (previous?.prefs) brand.prefs = previous.prefs;

  return brand;
};

export const BrandProfileDialog = ({
  agency,
  profile,
  open,
  onOpenChange,
  onSaved,
}: {
  agency: Agency;
  profile?: AgencyBrandProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [validationError, setValidationError] = useState<string | null>(null);

  const parsed = profile ? safeParseBrandProfile(profile.brand) : null;
  const existingBrand = parsed && parsed.ok ? parsed.profile : null;

  useEffect(() => {
    if (open) {
      setForm(formFromBrand(existingBrand));
      setValidationError(null);
    }
    // Re-seed the form each time the dialog opens for the current profile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile?.id]);

  const set = (key: keyof FormState) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const toggleAdjective = (adjective: string) =>
    setForm((current) => ({
      ...current,
      adjectives: current.adjectives.includes(adjective)
        ? current.adjectives.filter((item) => item !== adjective)
        : current.adjectives.length < 5
          ? [...current.adjectives, adjective]
          : current.adjectives,
    }));

  const save = () => {
    const document = brandFromForm(form, existingBrand);
    try {
      parseBrandProfile(document);
    } catch (error) {
      setValidationError(
        String((error as Error).message ?? error).slice(0, 400),
      );
      return;
    }
    setValidationError(null);
    const updated_by = `crm:${identity?.id ?? "unknown"}`;
    const callbacks = {
      onSuccess: () => {
        notify(
          "Brand saved — applies to the members' next echoes (bot cache ~1 minute).",
          { type: "info" },
        );
        onSaved();
        onOpenChange(false);
      },
      onError: () =>
        notify("Could not save the brand profile — please try again.", {
          type: "warning",
        }),
    };
    if (profile) {
      update(
        "agency_brand_profiles",
        {
          id: profile.id,
          data: { brand: document, updated_by },
          previousData: profile,
        },
        callbacks,
      );
    } else {
      create(
        "agency_brand_profiles",
        {
          data: {
            agency_id: agency.id,
            brand: document,
            schema_version: 1,
            updated_by,
          },
        },
        callbacks,
      );
    }
  };

  const hexHint = (value: string) =>
    value.trim() && !HEX_RE.test(value.trim()) ? "expected #RRGGBB" : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Brand guidelines — {agency.name}</DialogTitle>
          <DialogDescription>
            Everything is optional: the profile is a sparse overlay and the
            pipeline behaves as today for anything left empty. Logo uploads
            happen in the agency portal.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-medium">Identity</h4>
            <Input
              placeholder="Display name — e.g. VDB Luxury Properties"
              value={form.display_name}
              onChange={(event) => set("display_name")(event.target.value)}
            />
            <Input
              placeholder="Tagline (optional)"
              value={form.tagline}
              onChange={(event) => set("tagline")(event.target.value)}
            />
          </section>

          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-medium">Palette</h4>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Primary — #0E2A3A"
                  value={form.primary}
                  onChange={(event) => set("primary")(event.target.value)}
                  className="font-mono"
                />
                {hexHint(form.primary) && (
                  <p className="text-xs text-destructive">
                    {hexHint(form.primary)}
                  </p>
                )}
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Accent — #A9863F"
                  value={form.accent}
                  onChange={(event) => set("accent")(event.target.value)}
                  className="font-mono"
                />
                {hexHint(form.accent) && (
                  <p className="text-xs text-destructive">
                    {hexHint(form.accent)}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-medium">Contact (closing card)</h4>
            <Input
              placeholder="Agent name — e.g. Kenneth Melchor"
              value={form.agent_name}
              onChange={(event) => set("agent_name")(event.target.value)}
            />
            <Input
              placeholder="Phone as displayed — e.g. +34 679 680 000"
              value={form.phone_display}
              onChange={(event) => set("phone_display")(event.target.value)}
            />
            <Input
              placeholder="Email (optional)"
              value={form.email}
              onChange={(event) => set("email")(event.target.value)}
            />
            <Input
              placeholder="Website as displayed (optional)"
              value={form.website_display}
              onChange={(event) => set("website_display")(event.target.value)}
            />
          </section>

          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-medium">
              Tone{" "}
              <span className="text-xs text-muted-foreground">(max 5)</span>
            </h4>
            <div className="flex flex-wrap gap-1">
              {TONE_VOCABULARY.map((adjective) => (
                <button
                  key={adjective}
                  type="button"
                  onClick={() => toggleAdjective(adjective)}
                >
                  <Badge
                    variant={
                      form.adjectives.includes(adjective)
                        ? "default"
                        : "outline"
                    }
                  >
                    {adjective}
                  </Badge>
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Notes (internal — never used in prompts)"
              value={form.notes}
              onChange={(event) => set("notes")(event.target.value)}
              maxLength={500}
              rows={3}
            />
          </section>

          {validationError && (
            <p className="text-sm text-destructive">{validationError}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={isCreating || isUpdating}>
            Save brand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
